import React, { FC } from "react";
import { MDXRenderer } from "gatsby-plugin-mdx";
import { MDXProvider } from "@mdx-js/react";
import { Button, ButtonSize, CopyToClipboard, IconButton } from "kaleidoscope/src";
import { Link as LinkIcon, Preview, OptionsVertical } from "kaleidoscope/src/global/icons";
import Heading, { HeadingWeight, HeadingProps } from "../../components/Heading";
import Text, { TextSize, TextWeight } from "../../components/Text";
import Link from "../../components/Link";
import PostCode from "./PostCode";
import { useLocation } from "@reach/router";
import { kebabCase } from "lodash";
import classNames from "classnames";

const MarkdownHeading: FC<HeadingProps> = ({ children, level, id, className, ...props }) => {
  const location = useLocation();
  const headingId = id || kebabCase(children.toString());
  const headingUrl = `${location.origin}${location.pathname}#${headingId}`;

  return (
    <Heading
      className={classNames("kld-post-markdown__heading", className)}
      level={level}
      id={headingId}
      weight={HeadingWeight.Strong}
      {...props}
    >
      <span className="kld-post-markdown__heading-inner">
        <span className="kld-post-markdown__heading-text">
          {children}
          <CopyToClipboard value={headingUrl} tooltip={{ content: "Copy link" }}>
            {({ onCopy }) => (
              <IconButton
                className="kld-post-markdown__heading-copy"
                icon={<LinkIcon />}
                size={ButtonSize.Small}
                onClick={onCopy}
              />
            )}
          </CopyToClipboard>
        </span>
      </span>
    </Heading>
  );
};

type MarkdownHeadingProps = Omit<HeadingProps, "level">;

export const MarkdownH1: FC<MarkdownHeadingProps> = (props) => <MarkdownHeading level={1} {...props} />;

export const MarkdownH2: FC<MarkdownHeadingProps> = (props) => <MarkdownHeading level={2} {...props} />;

export const MarkdownH3: FC<MarkdownHeadingProps> = (props) => <MarkdownHeading level={3} {...props} />;

export const MarkdownH4: FC<MarkdownHeadingProps> = (props) => <MarkdownHeading level={4} {...props} />;

const MarkdownText: FC = (props) => (
  <Text className="kld-post-markdown__text" as="p" size={TextSize.XLarge} {...props} />
);

export const MarkdownImage: FC<{ src: string; alt: string }> = (props) => (
  <img className="kld-post-markdown__image" {...props} />
);

const MarkdownUl: FC = (props) => (
  <ul className="kld-post-markdown__list kld-post-markdown__list--unordered" {...props} />
);

const MarkdownOl: FC = (props) => (
  <ol className="kld-post-markdown__list kld-post-markdown__list--ordered" {...props} />
);

const MarkdownLi: FC = ({ children, ...props }) => (
  <li className="kld-post-markdown__list-item" {...props}>
    <Text size={TextSize.XLarge}>{children}</Text>
  </li>
);

export const MarkdownInlineCode: FC = (props) => <code className="kld-post-markdown__inline-code" {...props} />;

export const MarkdownTable: FC = ({ children, ...props }) => (
  <table className="kld-post-markdown__table" {...props}>
    <tbody>{children}</tbody>
  </table>
);

export const MarkdownTableHead: FC = (props) => <thead className="kld-post-markdown__table-head" {...props} />;

export const MarkdownTableRow: FC = (props) => <tr className="kld-post-markdown__table-row" {...props} />;
export const MarkdownTableHeadCell: FC = ({ children, ...props }) => (
  <th className="kld-post-markdown__table-head-cell" {...props}>
    <Text size={TextSize.XLarge} weight={TextWeight.Strong}>
      {children}
    </Text>
  </th>
);

export const MarkdownTableCell: FC = ({ children, ...props }) => (
  <td className="kld-post-markdown__table-cell" {...props}>
    <Text size={TextSize.XLarge}>{children}</Text>
  </td>
);

const MarkdownHr: FC = (props) => <hr className="kld-post-markdown__hr" {...props} />;

export const MarkdownBlockquote: FC = (props) => <blockquote className="kld-post-markdown__blockquote" {...props} />;

interface IPostMarkdownProps {
  children: string;
}

export const MDXComponents = {
  Button,
  Preview,
  h1: MarkdownH1,
  h2: MarkdownH2,
  h3: MarkdownH3,
  h4: MarkdownH4,
  p: MarkdownText,
  img: MarkdownImage,
  ul: MarkdownUl,
  ol: MarkdownOl,
  li: MarkdownLi,
  code: PostCode,
  inlineCode: MarkdownInlineCode,
  a: Link,
  table: MarkdownTable,
  thead: MarkdownTableHead,
  tr: MarkdownTableRow,
  th: MarkdownTableHeadCell,
  td: MarkdownTableCell,
  hr: MarkdownHr,
  blockquote: MarkdownBlockquote,
  OptionsVertical,
};

const PostMarkdown: FC<IPostMarkdownProps> = ({ children }) => {
  return (
    <div className="kld-post-markdown">
      <MDXProvider components={MDXComponents}>
        <MDXRenderer>{children}</MDXRenderer>
      </MDXProvider>
    </div>
  );
};

export default PostMarkdown;
